// Components/Pages/Gallery.jsx
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight, 
  FaCamera,
  FaImage
} from "react-icons/fa";

const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState("All");

  // Fetch gallery function
  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.rigokenya.org/api/gallery');
      if (!response.ok) {
        throw new Error('Failed to fetch gallery');
      }
      const data = await response.json();
      setGalleryImages(data);
      setError("");
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setError('Failed to load gallery. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // Get unique categories from images
  const getCategories = () => {
    const categories = galleryImages.map(img => img.category).filter(Boolean);
    return ["All", ...new Set(categories)];
  };

  const categories = getCategories();

  const filteredImages = activeCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    const actualIndex = galleryImages.indexOf(filteredImages[index]);
    setCurrentIndex(actualIndex);
    setSelectedImage(galleryImages[actualIndex]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = useCallback((direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < galleryImages.length) {
      setCurrentIndex(newIndex);
      setSelectedImage(galleryImages[newIndex]);
    }
  }, [currentIndex, galleryImages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === "Escape") closeLightbox();
        if (e.key === "ArrowLeft") navigateImage(-1);
        if (e.key === "ArrowRight") navigateImage(1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, navigateImage]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchGallery}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Gallery
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            A visual journey through our work and impact in communities across Western Kenya.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Category Filters */}
        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category 
                    ? "bg-primaryGold text-primaryBlue shadow-md" 
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        {filteredImages.length === 0 ? (
          <div className="text-center py-12">
            <FaCamera className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {activeCategory === "All" 
                ? "No images uploaded yet." 
                : `No images found in "${activeCategory}" category.`}
            </p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for updates.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {filteredImages.map((image, index) => (
              <div 
                key={image.id}
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white"
              >
                <div className="relative h-64 bg-gray-100">
                  {image.image_url ? (
                    <img 
                      src={`https://api.rigokenya.org${image.image_url}`}
                      alt={image.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x300/1a365d/ffffff?text=Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <FaImage className="text-6xl" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <h3 className="text-white font-bold text-lg mb-1">{image.title}</h3>
                    {image.description && (
                      <p className="text-white/80 text-sm line-clamp-2">{image.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-white/70">{image.category || 'General'}</span>
                      <span className="text-xs text-white/70">{image.date || ''}</span>
                    </div>
                  </div>
                  
                  {/* Category Badge */}
                  {image.category && (
                    <div className="absolute top-4 left-4 bg-primaryGold text-primaryBlue text-xs font-semibold px-3 py-1 rounded-full">
                      {image.category}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <div 
              className="relative max-w-5xl w-full bg-white rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all duration-300"
              >
                <FaTimes className="text-xl" />
              </button>

              {/* Navigation Buttons */}
              <button
                onClick={() => navigateImage(-1)}
                className={`absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 ${
                  currentIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={currentIndex === 0}
              >
                <FaChevronLeft className="text-xl" />
              </button>

              <button
                onClick={() => navigateImage(1)}
                className={`absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 ${
                  currentIndex === galleryImages.length - 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={currentIndex === galleryImages.length - 1}
              >
                <FaChevronRight className="text-xl" />
              </button>

              {/* Image Display in Lightbox */}
              <div className="flex items-center justify-center min-h-[60vh] bg-gray-100 p-4">
                {selectedImage.image_url ? (
                  <img 
                    src={`https://api.rigokenya.org${selectedImage.image_url}`}
                    alt={selectedImage.title}
                    className="max-h-[70vh] max-w-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800x600/1a365d/ffffff?text=Image';
                    }}
                  />
                ) : (
                  <div className="text-gray-400">
                    <FaImage className="text-8xl mx-auto mb-4" />
                    <p className="text-center text-gray-500">No image available</p>
                  </div>
                )}
              </div>

              {/* Image Info */}
              <div className="p-6 bg-white">
                <h3 className="text-2xl font-bold text-primaryBlue mb-2">
                  {selectedImage.title}
                </h3>
                {selectedImage.description && (
                  <p className="text-gray-600 mb-4">
                    {selectedImage.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  {selectedImage.category && (
                    <span className="flex items-center gap-2">
                      <span className="font-medium">Category:</span>
                      <span className="bg-primaryGold/10 text-primaryGold px-3 py-1 rounded-full">
                        {selectedImage.category}
                      </span>
                    </span>
                  )}
                  {selectedImage.location && <span>📍 {selectedImage.location}</span>}
                  {selectedImage.date && <span>📅 {selectedImage.date}</span>}
                  <span className="ml-auto text-xs">
                    {currentIndex + 1} / {galleryImages.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Want to share your photos or collaborate with us?</p>
          <Link
            to="/contact"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Gallery;